import * as React from "react";
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Typography from "@mui/joy/Typography";
import { ModalDialog, ModalClose, Stack } from "@mui/joy";
import Modal from "@mui/joy/Modal";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LinkIcon from "@mui/icons-material/Link";
import Input from "@mui/joy/Input";
import { FormControl } from "@mui/material";
import TourIcon from "@mui/icons-material/Tour";
import Confetti from "react-confetti";
import { UserAuth } from "../../context/AuthContext";
import { db } from "../../FirebaseConfig";
import { v4 as uuidv4 } from "uuid";

interface CustomCardProps {
  title?: string;
  color?: any;
  difficulty?: string;
  difficultyColor?: any;
  category?: string;
  points?: string;
  data?: any;
  onSolve: (solvedChallenge: any) => void;
}

const CustomCard = (props: CustomCardProps) => {
  const [open, setOpen] = React.useState(false);
  const [selectedData, setSelectedData] = React.useState<any>({});
  const [flag, setFlag] = React.useState("");
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [isCorrect, setIsCorrect] = React.useState(false);
  const [solvedChallenges, setSolvedChallenges] = React.useState<Set<string>>(new Set());
  const { userData } = UserAuth();
  const uid = uuidv4();

  React.useEffect(() => {
    // Fetch the solved challenges for the current user
    const fetchSolvedChallenges = async () => {
      if (userData?.id) {
        const submissionSnapshot = await db
          .collection("ChallengeSubmissions")
          .where("userid", "==", userData?.id)
          .get();

        const solvedSet = new Set<string>();
        submissionSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.name) {
            solvedSet.add(data.name);
          }
        });
        setSolvedChallenges(solvedSet);
      }
    };

    fetchSolvedChallenges();
  }, [userData]);

  const handleOpenModal = (challengeData: any) => {
    setSelectedData(challengeData);
    setOpen(true);

    // Check if the challenge has already been solved
    if (solvedChallenges.has(challengeData.name)) {
      setIsCorrect(true);
    } else {
      setIsCorrect(false);
    }
  };

  const handleSubmitFlag = async () => {
    if (solvedChallenges.has(selectedData.name)) {
      alert("You have already submitted this challenge.");
      return;
    }

    if (flag === selectedData.flag) {
      setShowConfetti(true);
      setIsCorrect(true);

      const solvedChallenge = {
        id: uid,
        userid: userData?.id,
        useremail: userData?.email,
        username: userData?.name,
        category: selectedData.category,
        points: selectedData.points,
        name: selectedData.name,
        flag: selectedData.flag,
        solvedAt: new Date(),
      };

      // Submit the challenge
      await db.collection("ChallengeSubmissions").doc(uid).set(solvedChallenge);
      props.onSolve(solvedChallenge);

      // Update solved challenges state
      setSolvedChallenges((prev) => new Set(prev).add(selectedData.name));

      setTimeout(() => {
        setShowConfetti(false);
        setOpen(false);
      }, 10000);
    } else {
      alert("Incorrect Flag!");
    }
  };

  return (
    <>
      <Card variant="soft" color={props.color}>
        <CardContent
          orientation="horizontal"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography level="title-md">{props.category}</Typography>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography level="title-md">Points</Typography>
            <Typography level="body-sm">{props.points}</Typography>
          </div>
        </CardContent>
        <Typography level="h2">{props.title}</Typography>
        <CardContent orientation="horizontal">
          <div style={{ textAlign: "center" }}>
            <Typography level="title-md">Difficulty</Typography>
            <Typography color={props.difficultyColor} level="title-sm">
              {props.difficulty}
            </Typography>
          </div>
          <Button
            variant="solid"
            size="md"
            color={props.color}
            aria-label="Explore"
            sx={{ ml: "auto", alignSelf: "center", fontWeight: 600 }}
            onClick={() => handleOpenModal(props.data)}
          >
            Explore
          </Button>
        </CardContent>
      </Card>
      <div>
        <Modal
          aria-labelledby="Challenge Modal"
          aria-describedby="This modal shows Challenge data."
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transform: "translateX(120px)",
          }}
        >
          <ModalDialog layout={"center"}>
            <Typography level="h2">Challenge Details</Typography>
            <ModalClose />
            <Box
              sx={{
                width: "700px",
                height: "500px",
                display: "flex",
                border: "1px solid",
                borderRadius: "7px",
                borderColor: "#363636",
              }}
            >
              <Stack spacing={1} width={"100%"} divider={<Divider />}>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg">Category</Typography>
                    <Typography level="body-lg">
                      {selectedData.category}{" "}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography level="title-lg">Points</Typography>
                    <Typography level="body-lg">
                      {selectedData.points}
                    </Typography>
                  </div>
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                    height: "60%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography level="h1" sx={{ mb: 3 }}>
                      {selectedData.name}
                    </Typography>
                    <Typography level="title-lg" sx={{ mb: 0.5 }}>
                      Description
                    </Typography>
                    <Typography level="body-lg">
                      {selectedData.description}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Typography level="title-lg">Hints</Typography>
                      <div>
                        {selectedData.hints &&
                          selectedData.hints.map((hint: string, index: number) => (
                            <Tooltip
                              key={index}
                              title={hint}
                              color="primary"
                              arrow
                              size="lg"
                              sx={{ mt: 1 }}
                            >
                              <IconButton>
                                <TipsAndUpdatesIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                          ))}
                      </div>
                    </div>

                    {selectedData.fileUrl ? (
                      ["Web Exploitation"].includes(selectedData.category) ? (
                        <Button
                          variant="plain"
                          startDecorator={<LinkIcon />} // Assuming a link icon for "View Site"
                          onClick={() => {
                            window.open(selectedData.fileUrl, "_blank"); // Replace with actual site URL
                          }}
                        >
                          View&nbsp;Site
                        </Button>                        
                      ) : (
                        <Button
                          variant="plain"
                          startDecorator={<FileDownloadIcon />}
                          onClick={() => {
                            window.open(selectedData.fileUrl, "_blank");
                          }}                          
                        >
                          Download
                        </Button>
                      )
                    ) : null}

                  </div>
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "left",
                      width: "100%",
                    }}
                  >
                    <Typography level="title-lg" sx={{ mb: 1 }}>
                      Flag
                    </Typography>
                    <FormControl id="flag" sx={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <Input
                          size="sm"
                          startDecorator={<TourIcon />}
                          placeholder="Enter Flag"
                          type="text"
                          sx={{ flexGrow: 1 }}
                          required
                          value={flag}
                          onChange={(e) => setFlag(e.target.value)}
                          disabled={isCorrect}
                        />
                        <Button
                          type="submit"
                          variant="outlined"
                          color={isCorrect ? "success" : "primary"}
                          onClick={handleSubmitFlag}
                          disabled={isCorrect}
                          sx={{
                            flexShrink: 0,
                            marginLeft: "16px",
                            "&:disabled": {
                              backgroundColor: isCorrect
                                ? "#4CAF50"
                                : "#1976D2",
                              color: "#ffffff",
                              opacity: 1,
                            },
                          }}
                        >
                          {isCorrect ? "Correct!" : "Submit Flag"}
                        </Button>
                      </div>
                    </FormControl>
                  </div>
                </Stack>
              </Stack>
            </Box>
            {showConfetti && <Confetti width={700} height={500} />}
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
};

export default CustomCard;
